import React, { useMemo } from 'react';
import { matchRoutes, NavLink, useLocation } from 'react-router-dom';
import { Breadcrumb } from 'antd';
import { token } from '@atlas-frontend/design-tokens';
import styled from 'styled-components';
import { cleanArray } from '@atlas-frontend/sdk';
import { onBasePath } from '../paths/onBasePath';
import { RouteObject } from './types';
import { Helmet } from 'react-helmet-async';

export interface BreadcrumbsProps<T = any> {
  routes: Array<RouteObject<T>>;
  basePath?: string;
  data?: T;
}

export const Breadcrumbs = <T = any,>(props: BreadcrumbsProps<T>) => {
  const { routes, basePath = '', data } = props;
  const { pathname } = useLocation();

  const routesOnBasePath = useMemo(
    () => onBasePath(basePath, routes),
    [basePath, routes]
  );

  const matches = useMemo(
    () =>
      matchRoutes(
        routesOnBasePath,
        pathname.replace(new RegExp(`^${basePath}`), '')
      )?.filter((match) => !match.route.index),
    [routesOnBasePath, pathname, basePath]
  );

  if (!matches?.length) {
    return null;
  }

  const title = matches[matches.length - 1].route.renderBreadcrumb?.(data);

  return (
    <>
      <Helmet>
        <title>{typeof title === 'string' ? title : '...'}</title>
      </Helmet>
      <StyledBreadcrumb
        items={matches.map((match, index) => {
          const isLast = index === matches.length - 1;
          const label = match.route.renderBreadcrumb?.(data) || '...';
          return {
            title: isLast ? (
              label
            ) : (
              <NavLink
                id={getBreadcrumbId(basePath, match.pathname)}
                to={basePath + match.pathname}
              >
                {label}
              </NavLink>
            ),
            match,
          };
        })}
      />
    </>
  );
};

const getBreadcrumbId = (...paths: string[]) =>
  cleanArray(
    paths.map((p) => p.replace(/^\/|\/$/g, '').replace(/\//g, '-'))
  ).join('-');

const StyledBreadcrumb = styled(Breadcrumb)`
  font-size: ${token.fontSizeSM}px;

  a {
    height: auto;
  }
`;
